import { F } from '@/utils/intl';
import { HistoryOutlined, SendOutlined } from '@ant-design/icons';
import { Button, Space } from 'antd';
import React from 'react';
import styles from './TemplateDrawer.less';

export interface TemplateDrawerFooterProps {
  isEdit: boolean;
  readOnly: boolean;
  /** Step 1 of creating: the only action is Next. */
  onCreateSettings: boolean;
  /** Step 2 of creating: Back, and Start designing. */
  onCreateStart: boolean;
  /** A direct save writes the live template — that needs publish rights. */
  canPublish: boolean;
  saving: boolean;
  /** The draft fetch on open; saving is held until it lands. */
  draftLoading: boolean;
  onOpenHistory: () => void;
  onOpenTestMail: () => void;
  onNext: () => void;
  onBack: (step: 0 | 1 | 2) => void;
  onStartDesigning: () => void;
  /** Disabled on step 2 until a base has actually been picked. */
  startDisabled: boolean;
  onSave: (asDraft: boolean) => void;
}

/**
 * Tools left, save actions right.
 *
 * Versions and Send test act on the template as a whole rather than on
 * anything in the editor, so they sit apart from Speichern instead of
 * competing with it in the header.
 *
 * There is no Cancel: the drawer closes by its ✕, by the overlay and by
 * Escape, so a button repeating that only crowds the two actions that
 * are not otherwise reachable.
 *
 * Rendered for a read-only template too — Send test still applies there,
 * since sending yourself a copy is reading it, not changing it.
 */
const TemplateDrawerFooter: React.FC<TemplateDrawerFooterProps> = ({
  isEdit,
  readOnly,
  onCreateSettings,
  onCreateStart,
  canPublish,
  saving,
  draftLoading,
  onOpenHistory,
  onOpenTestMail,
  onNext,
  onBack,
  onStartDesigning,
  startDisabled,
  onSave,
}) => (
  <div className={styles.footer}>
    <Space size={4}>
      {/* Only for a saved, owned template: a global one is
          product-managed and has no restorable history. */}
      {isEdit && !readOnly && (
        <Button type="text" icon={<HistoryOutlined />} onClick={onOpenHistory}>
          <F
            id="pages.emailCenter.templates.historyAction"
            defaultMessage="Versions"
          />
        </Button>
      )}
      {isEdit && (
        <Button type="text" icon={<SendOutlined />} onClick={onOpenTestMail}>
          <F
            id="pages.emailCenter.templates.sendTest"
            defaultMessage="Send test"
          />
        </Button>
      )}
    </Space>

    {onCreateSettings ? (
      <Button type="primary" onClick={onNext}>
        <F id="pages.emailCenter.templates.create.next" defaultMessage="Next" />
      </Button>
    ) : onCreateStart ? (
      <Space size={8}>
        <Button onClick={() => onBack(0)}>
          <F
            id="pages.emailCenter.templates.create.back"
            defaultMessage="Back to settings"
          />
        </Button>
        <Button
          type="primary"
          disabled={startDisabled}
          onClick={onStartDesigning}
        >
          <F
            id="pages.emailCenter.templates.create.startDesigning"
            defaultMessage="Start designing"
          />
        </Button>
      </Space>
    ) : (
      !readOnly && (
        /**
         * Saving is held until the draft has loaded. Otherwise a fast click
         * writes the *published* values that are on screen for that instant,
         * silently overwriting the draft it was about to replace them with.
         */
        <Space size={8}>
          {!isEdit && (
            <Button onClick={() => onBack(1)}>
              <F
                id="pages.emailCenter.templates.create.backAction"
                defaultMessage="Back"
              />
            </Button>
          )}
          {isEdit && (
            <Button
              loading={saving}
              disabled={draftLoading}
              onClick={() => onSave(true)}
            >
              <F
                id="pages.emailCenter.templates.saveAsDraft"
                defaultMessage="Save as draft"
              />
            </Button>
          )}
          {/* A direct save writes the live template — publish
              permission (EmailTemplatePolicy::update). Draft-only
              users keep "Save as draft". */}
          {(!isEdit || canPublish) && (
            <Button
              type="primary"
              loading={saving || draftLoading}
              disabled={draftLoading}
              onClick={() => onSave(false)}
            >
              <F id="common.actions.save" defaultMessage="Save" />
            </Button>
          )}
        </Space>
      )
    )}
  </div>
);

export default TemplateDrawerFooter;
